import { createTheme, ThemeProvider } from "@mui/material/styles";
import type {} from "@mui/material/themeCssVarsAugmentation";
import React from "react";

import { colorSchemes } from "./utils/theme/color-schemes";
import { typography } from "./utils/theme/typography";

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    blue: true;
    red: true;
    white: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    grey: true;
    lavender: true;
    red: true;
  }
}

declare module "@mui/material/styles" {
  interface Components {
    MuiPickersYear?: {
      styleOverrides?: {
        yearButton?: React.CSSProperties;
      };
    };
  }
}

declare module "@mui/material/styles" {
  interface Components {
    MuiPickersMonth?: {
      styleOverrides?: {
        monthButton?: React.CSSProperties;
      };
    };
  }
}

const theme = createTheme({
  cssVariables: {
    colorSchemeSelector: "class",
  },
  typography,
  colorSchemes,
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiPickersMonth: {
      styleOverrides: {
        monthButton: {
          fontSize: "0.875rem",
        },
      },
    },
    MuiPickersYear: {
      styleOverrides: {
        yearButton: {
          fontSize: "0.875rem",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }: any) => ({
          // ...(theme.vars.color === "white" && {
          //   color: theme.vars.palette.white.contrastText,
          //   backgroundColor: theme.vars.palette.white,
          //   "&:hover": {
          //     backgroundColor: theme.vars.palette.white.dark,
          //   },
          // }),
          textTransform: "none",
          borderRadius: 96,
          py: 8,
          px: 24,
          variants: [
            {
              props: { variant: "contained" },
              style: {
                boxShadow:
                  "0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
                "&:active": {
                  boxShadow: "none",
                },
              },
            },
            {
              props: { variant: "contained", color: "primary" },
              style: {
                "&:hover, &:active": {
                  backgroundColor: theme.vars.palette.primary[900],
                },
              },
            },
            {
              props: { variant: "outlined", color: "primary" },
              style: {
                "&:hover, &:active": {
                  backgroundColor: theme.vars.palette.primary[100],
                },
              },
            },
            {
              props: { variant: "contained", color: "secondary" },
              style: {
                backgroundColor: theme.vars.palette.primary[100],
                color: theme.vars.palette.primary.main,
                "&:hover, &:active": {
                  backgroundColor: theme.vars.palette.primary[300],
                },
              },
            },
            {
              props: { color: "white" },
              style: {
                color: theme.vars.palette.white.contrastText,
                backgroundColor: theme.vars.palette.white.main,
                "&:hover": {
                  backgroundColor: theme.vars.palette.white.dark,
                },
              },
            },
            {
              props: { color: "blue" },
              style: {
                color: theme.vars.palette.blue.contrastText,
                backgroundColor: theme.vars.palette.blue.main,
                "&:hover": {
                  backgroundColor: theme.vars.palette.blue.dark,
                },
              },
            },
            {
              props: { color: "red" },
              style: {
                color: theme.vars.palette.error.dark,
                backgroundColor: theme.vars.palette.error.light,
                "&:hover": {
                  backgroundColor: "#ffa8a2",
                },
              },
            },
          ],
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          color: theme.vars.palette.grey[950],
          fontSize: theme.typography.body2.fontSize,
          "& .MuiFormLabel-asterisk": {
            color: theme.vars.palette.error.main,
          },
        }),
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          // color: theme.vars.palette.grey[950],
          fontSize: theme.typography.body2.fontSize,
          gap: 16,
          "& .MuiFormControlLabel-asterisk": {
            color: theme.vars.palette.error.main,
          },
          "& .MuiTypography-root": {
            // color: theme.vars.palette.grey[950],
            fontSize: theme.typography.body2.fontSize,
          },
        }),
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          "& .MuiInputBase-root, & .MuiInputLabel-root, & .MuiInputBase-input":
            {
              fontSize: theme.typography.body2.fontSize,
            },
          "& .MuiOutlinedInput-notchedOutline": {
            marginTop: 5,
          },
          "& .MuiOutlinedInput-notchedOutline legend": {
            display: "none",
          },
          "& .MuiInputLabel-root + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
            {
              marginTop: 0,
            },
          "& .MuiInputLabel-root + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend":
            {
              display: "block",
            },
          "& .MuiFormHelperText-root": {
            marginLeft: 0,
            marginRight: 0,
          },
          // "& .MuiInputBase-root.Mui-disabled": {
          //   "& .MuiInputBase-input": {
          //     color: theme.vars.palette.grey[400],
          //     "-webkit-text-fill-color": theme.vars.palette.grey[400],
          //   },
          // },
          // "& .MuiInputLabel-root.Mui-disabled": {
          //   color: theme.vars.palette.grey[400],
          // },
        }),
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          fontSize: theme.typography.body2.fontSize,
        }),
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popper: () => ({
          "& .MuiAutocomplete-listbox": {
            fontSize: theme.typography.body2.fontSize,
          },
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          color: theme.vars.palette.grey[950],
        }),
      },
    },
    MuiTable: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          "& .MuiTableCell-root": {
            color: theme.vars.palette.grey[950],
          },
        }),
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          color: theme.vars.palette.grey[950],
        }),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          color: theme.vars.palette.grey[950],
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          borderRadius: 4,
          backgroundColor: theme.vars.palette.grey[50],
          "& .MuiChip-label": {
            color: theme.vars.palette.grey[950],
            fontSize: theme.typography.overline.fontSize,
          },
          variants: [
            {
              props: { variant: "filled", color: "info" },
              style: {
                backgroundColor: theme.vars.palette.secondary[100],
                "& .MuiChip-label": {
                  color: theme.vars.palette.info.dark,
                },
              },
            },
            {
              props: { variant: "filled", color: "success" },
              style: {
                backgroundColor: theme.vars.palette.success.light,
                "& .MuiChip-label": {
                  color: theme.vars.palette.success.dark,
                },
              },
            },
            {
              props: { variant: "filled", color: "warning" },
              style: {
                backgroundColor: theme.vars.palette.warning.light,
                "& .MuiChip-label": {
                  color: theme.vars.palette.warning.dark,
                },
              },
            },
            {
              props: { variant: "filled", color: "error" },
              style: {
                backgroundColor: theme.vars.palette.error.light,
                "& .MuiChip-label": {
                  color: theme.vars.palette.error.dark,
                },
              },
            },
            {
              props: { variant: "filled", color: "red" },
              style: {
                backgroundColor: theme.vars.palette.error.main,
                "& .MuiChip-label": {
                  color: theme.vars.palette.error.contrastText,
                },
              },
            },
            {
              props: { variant: "filled", color: "grey" },
              style: {
                backgroundColor: theme.vars.palette.grey[950],
                "& .MuiChip-label": {
                  color: theme.vars.palette.grey[50],
                },
              },
            },
            {
              props: { variant: "filled", color: "secondary" },
              style: {
                backgroundColor: theme.vars.palette.secondary[600],
                "& .MuiChip-label": {
                  color: theme.vars.palette.secondary.contrastText,
                },
              },
            },
            {
              props: { variant: "filled", color: "lavender" },
              style: {
                backgroundColor: theme.vars.palette.lavender.light,
                "& .MuiChip-label": {
                  color: theme.vars.palette.lavender.dark,
                },
              },
            },
            {
              props: { size: "large" },
              style: {
                "& .MuiChip-label": {
                  fontSize: theme.typography.body1.fontSize,
                },
              },
            },
          ],
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          "& .MuiDialogActions-root": {
            padding: "0px 24px 20px",
          },
        }),
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          fontSize: theme.typography.body1.fontSize,
          fontWeight: "bold",
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          fontWeight: "bold",
          variants: [
            {
              props: { variant: "filled", severity: "success" },
              style: {
                backgroundColor: theme.vars.palette.success.light,
                color: theme.vars.palette.success.dark,
              },
            },
            {
              props: { variant: "filled", severity: "error" },
              style: {
                backgroundColor: theme.vars.palette.error.light,
                color: theme.vars.palette.error.dark,
              },
            },
            {
              props: { variant: "filled", severity: "info" },
              style: {
                backgroundColor: theme.vars.palette.info.light,
                color: theme.vars.palette.info.dark,
              },
            },
            {
              props: { variant: "filled", severity: "warning" },
              style: {
                backgroundColor: theme.vars.palette.warning.light,
                color: theme.vars.palette.warning.dark,
              },
            },
          ],
        }),
      },
    },
    MuiList: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          "&.MuiMenu-list": {
            "& .MuiMenuItem-root": {
              fontSize: theme.typography.button.fontSize,
            },
          },
        }),
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }: { theme: any }) => ({
          fontSize: theme.typography.overline.fontSize,
        }),
      },
    },

    // MuiList: {
    //   styleOverrides: {
    //     root: ({ theme }: { theme: any }) => ({
    //       "&.MuiMenu-list": {
    //         backgroundColor: theme.vars.palette.primary.main,
    //         color: theme.vars.palette.primary.contrastText,
    //         "& .MuiMenuItem-root": {
    //           fontSize: theme.typography.button.fontSize,
    //           "&:hover, &:active": {
    //             backgroundColor: theme.vars.palette.secondary.main,
    //           },
    //           "& .MuiListItemIcon-root": {
    //             color: theme.vars.palette.primary.contrastText,
    //           },
    //         },
    //       },
    //     }),
    //   },
    // },
  },
});

function EssilorTheme({ children }: React.PropsWithChildren) {
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

export default EssilorTheme;
